import {
  CoreInputModelUtil,
  getNewErrors,
  InputErrorModel,
  IOption,
  UtilityMethods,
} from "@mindmemobile/mm-shared-global";
import { autorun } from "mobx";
import {
  CheckOutPayButtonCaption,
  CheckOutPayFields,
  CheckOutPromoCodeSelectionType,
  SellerTermsAndConditionsType,
  SurchargeType,
} from "../enums";
import {
  IAPICheckOutFormDataModel,
  IAPICheckOutFormFields,
  IAPICheckOutPayModel,
  IDynamicField,
} from "../interfaces";
import { IAPIPromotionModel } from "../interfaces/IAPIPromotionModels";
import { PromotionModel } from "./PromotionModels";

export class CheckoutPaymentPageModel {
  formData: CheckoutPaymentFormModel;
  errors: InputErrorModel<CheckoutPaymentPageModel>[];
  isTermsConditions: boolean;
  isAlternateCurrencies: boolean;
  allowTaxId: boolean;
  isUpdatesOffers: boolean;
  isPromotionCodes: boolean;
  isTestimonial: boolean;
  isRefundPolicy: boolean;
  refundPolicyValue: string;
  requireAcceptance: boolean;
  showTimer: boolean;
  promoCodeSelectionType: keyof typeof CheckOutPromoCodeSelectionType;
  applyCodeValue: IOption;
  isMoneyBack: boolean;
  testimonials: string[];
  promocodes: IOption[];
  moneyBackDuration: string;
  sellerTermsAndConditions: keyof typeof SellerTermsAndConditionsType;
  sellerReturnPolicy: keyof typeof SellerTermsAndConditionsType;
  surchargeType: keyof typeof SurchargeType;
  surchargeText: string;
  applyCodePromotionDetails?: PromotionModel;
  constructor(data?: Partial<CheckoutPaymentPageModel>) {
    this.formData = data?.formData || new CheckoutPaymentFormModel();
    this.isTermsConditions = data?.isTermsConditions || false;
    this.isAlternateCurrencies = data?.isAlternateCurrencies || false;
    this.allowTaxId = data?.allowTaxId || false;
    this.isUpdatesOffers = data?.isUpdatesOffers ?? false;
    this.isPromotionCodes = data?.isPromotionCodes || false;
    this.isTestimonial = data?.isTestimonial || false;
    this.isRefundPolicy = data?.isRefundPolicy || false;
    this.refundPolicyValue = data?.refundPolicyValue || "";
    this.surchargeText =
      data?.surchargeText ||
      "Yes, I want to cover the cost to process my payment.";
    this.requireAcceptance = data?.requireAcceptance || false;
    this.showTimer = data?.showTimer || false;
    this.applyCodeValue =
      data?.applyCodeValue || ({ label: "", value: "" } as IOption);
    this.promoCodeSelectionType =
      data?.promoCodeSelectionType ||
      CheckOutPromoCodeSelectionType.AllActiveCodes;
    this.isMoneyBack = data?.isMoneyBack || false;
    this.testimonials = data?.testimonials || [];
    this.promocodes = data?.promocodes || [];
    this.moneyBackDuration = data?.moneyBackDuration || "";
    this.sellerTermsAndConditions =
      data?.sellerTermsAndConditions || "DisplayOnly";
    this.sellerReturnPolicy = data?.sellerReturnPolicy || "DisplayOnly";
    this.surchargeType = data?.surchargeType || "IncludeSurcharge";
    this.applyCodePromotionDetails =
      data?.applyCodePromotionDetails || undefined;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CheckoutPaymentPageModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof CheckoutPaymentPageModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckoutPaymentPageModel>[] = [];
    const isValidForm = this.formData.validate(isAutoRun);
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0 && isValidForm;
  }

  createApiModel(): IAPICheckOutPayModel {
    const promoCodes =
      this.promoCodeSelectionType === "SelectCodes"
        ? this.promocodes.map(({ value, label }) => ({
            id: value,
            code: label,
          }))
        : this.promoCodeSelectionType === "ApplyCode" &&
            this.applyCodeValue.value
          ? [{ id: this.applyCodeValue.value, code: this.applyCodeValue.label }]
          : [];
    return {
      ...this.formData.createApiModel(),
      termsAndConditionsAcceptance: this.isTermsConditions,
      priceInMultiCurrencies: this.isAlternateCurrencies,
      allowTaxId: this.allowTaxId,
      updatesOnSpecialOffers: this.isUpdatesOffers,
      allowPromoCodes: this.isPromotionCodes,
      showTestimonial: this.isTestimonial,
      showMoneyBackGuarantee: this.isMoneyBack,
      testimonialIds: this.testimonials,
      promoCode: {
        selectionType: this.promoCodeSelectionType,
        promoCodes: promoCodes,
      },
      displayRefundPolicy: this.isRefundPolicy,
      requireRefundPolicyAcceptance: this.requireAcceptance,
      showTimer: this.showTimer,
      surchargeType: this.surchargeType,
      surchargePermissionText: this.surchargeText,
      sellerTermsAndConditionsType: this.sellerTermsAndConditions,
      sellerReturnPolicyType: this.sellerReturnPolicy,
    };
  }

  static deserializeAPIModel(
    data: IAPICheckOutPayModel,
    applyPromoCodeValue?: IAPIPromotionModel
  ): CheckoutPaymentPageModel {
    const { promoCode } = data;
    const mappedData: Partial<CheckoutPaymentPageModel> = {
      formData: CheckoutPaymentFormModel.deserializeAPIModel(data),
      isTermsConditions: data.termsAndConditionsAcceptance,
      isAlternateCurrencies: data.priceInMultiCurrencies,
      allowTaxId: data.allowTaxId,
      isUpdatesOffers: data.updatesOnSpecialOffers,
      isPromotionCodes: data.allowPromoCodes,
      isTestimonial: data.showTestimonial,
      isMoneyBack: data.showMoneyBackGuarantee,
      testimonials: data.testimonialIds,
      isRefundPolicy: data.displayRefundPolicy,
      requireAcceptance: data.requireRefundPolicyAcceptance,
      showTimer: data.showTimer,
      promoCodeSelectionType: data.promoCode?.selectionType,
      surchargeType: data.surchargeType,
      surchargeText: data.surchargePermissionText,
      sellerTermsAndConditions: data.sellerTermsAndConditionsType,
      sellerReturnPolicy: data.sellerReturnPolicyType,
      applyCodeValue:
        promoCode &&
        promoCode?.selectionType === "ApplyCode" &&
        promoCode?.promoCodes.length > 0
          ? {
              label: promoCode?.promoCodes[0].code,
              value: promoCode?.promoCodes[0].id,
            }
          : undefined,
      promocodes:
        promoCode && promoCode?.selectionType === "SelectCodes"
          ? promoCode?.promoCodes.map(({ id, code }) => ({
              label: code,
              value: id,
            }))
          : [],
      applyCodePromotionDetails:
        applyPromoCodeValue &&
        PromotionModel.deserializeAPIModel(applyPromoCodeValue),
    };

    return new CheckoutPaymentPageModel(mappedData);
  }
}

export class CheckOutPayPageFormFields {
  id: string;
  field: keyof typeof CheckOutPayFields | string;
  isRequired: boolean;

  constructor(data?: Partial<CheckOutPayPageFormFields>) {
    this.id = data?.id || UtilityMethods.uniqueId(5);
    this.field = data?.field || "";
    this.isRequired = data?.isRequired || false;
  }

  createApiModel(): IAPICheckOutFormFields {
    return {
      field: this.field as keyof typeof CheckOutPayFields,
      isRequired: this.isRequired,
    };
  }

  static deserializeAPIModel(
    data: IAPICheckOutFormFields
  ): CheckOutPayPageFormFields {
    const mappedData: Partial<CheckOutPayPageFormFields> = {
      field: data.field,
      isRequired: data.isRequired,
    };
    return new CheckOutPayPageFormFields(mappedData);
  }
}

export const initialPayPageFormFields: CheckOutPayPageFormFields[] = [
  new CheckOutPayPageFormFields({
    field: CheckOutPayFields.FirstLastName,
    isRequired: true,
  }),
  new CheckOutPayPageFormFields({
    field: CheckOutPayFields.EmailAddress,
    isRequired: true,
  }),
  new CheckOutPayPageFormFields({
    field: CheckOutPayFields.MobileNumber,
    isRequired: true,
  }),
  new CheckOutPayPageFormFields({
    field: CheckOutPayFields.TaxId,
    isRequired: false,
  }),
];

export class CheckoutPaymentFormModel {
  formFields: CheckOutPayPageFormFields[];
  isBillingAddressChecked: boolean;
  isShippingAddressChecked: boolean;
  buttonValue: string;
  payButtonType: keyof typeof CheckOutPayButtonCaption;
  errors: InputErrorModel<CheckoutPaymentFormModel>[];
  constructor(data?: Partial<CheckoutPaymentFormModel>) {
    this.formFields = (data?.formFields || initialPayPageFormFields).map(
      (field) => new CheckOutPayPageFormFields(field)
    );
    this.isBillingAddressChecked = data?.isBillingAddressChecked ?? false;
    this.isShippingAddressChecked = data?.isShippingAddressChecked ?? false;
    this.buttonValue = data?.buttonValue || "";
    this.payButtonType = data?.payButtonType || CheckOutPayButtonCaption.Pay;
    this.errors =
      (data &&
        data.errors?.map((e) => {
          return new InputErrorModel<CheckoutPaymentFormModel>(e);
        })) ||
      [];

    autorun(() => {
      this.validate(true);
    });
  }

  hasError(field: keyof CheckoutPaymentFormModel): boolean {
    return this.errors.some((e) => e.field === field);
  }

  validate(isAutoRun = false): boolean {
    const newErrors: InputErrorModel<CheckoutPaymentFormModel>[] = [];
    if (this.payButtonType === CheckOutPayButtonCaption.Custom) {
      CoreInputModelUtil.validateTextField(
        this.payButtonType,
        "payButtonType",
        newErrors
      );
      CoreInputModelUtil.validateTextField(
        this.buttonValue,
        "buttonValue",
        newErrors
      );
    }
    this.errors = isAutoRun ? getNewErrors(newErrors, this.errors) : newErrors;
    return this.errors.length === 0;
  }

  createApiModel(): IAPICheckOutFormDataModel {
    return {
      fields: this.formFields.map((field) => field.createApiModel()),
      collectBillingAddress: this.isBillingAddressChecked,
      collectShippingAddress: this.isShippingAddressChecked,
      isCustomButtonCaption:
        this.payButtonType === CheckOutPayButtonCaption.Custom,
      buttonCustomCaption: this.buttonValue,
      payButtonType: this.payButtonType,
    };
  }

  static deserializeAPIModel(
    data: IAPICheckOutFormDataModel
  ): CheckoutPaymentFormModel {
    const mappedData: Partial<CheckoutPaymentFormModel> = {
      formFields: data.fields.map((field) =>
        CheckOutPayPageFormFields.deserializeAPIModel(field)
      ),
      isBillingAddressChecked: data.collectBillingAddress,
      isShippingAddressChecked: data.collectShippingAddress,
      buttonValue: data.isCustomButtonCaption ? data.buttonCustomCaption : "",
      payButtonType: data.payButtonType,
    };
    return new CheckoutPaymentFormModel(mappedData);
  }
}

 

export const defaultAddedFields: IDynamicField[] = [
  {
    id: "birthday",
    type: "Date",
    value: "",
    checked: false,
    label: "",
  },
  {
    id: "renewalDate",
    type: "Date",
    value: "",
    checked: false,
    label: "",
  },
];